import { MultiSelectSearch } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import FieldHelpIcon from '../FieldHelpIcon'
import Pill from '../Pill'
import PillList from '../PillList'

const propTypes = {
  /**
   * id of the multi select
   */
  id: PropTypes.string.isRequired,
  /**
   * Title of the field. Must be a FormattedMessage id
   */
  title: PropTypes.string,
  /**
   * options of the multi select
   */
  options: PropTypes.arrayOf(PropTypes.shape).isRequired,
  /**
   * Callback called when an option is selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Selected values
   */
  values: PropTypes.arrayOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to format label content, should return an react node
   */
  formatOptionLabel: PropTypes.func,
  /**
   * validation errors messages
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Class to style the title
   */
  titleClassName: PropTypes.string,
  /**
   * Optional content rendered next to the field title
   */
  titleAction: PropTypes.node,
  /**
   * Indicates if field is required
   */
  required: PropTypes.bool,
  /**
   * Class to override default MultiSelectSearch styles
   */
  className: PropTypes.string,
  /**
   * When true, hides the selected value pills below the input
   */
  hidePills: PropTypes.bool,
  /**
   * Indicates if field is disabled
   */
  isDisabled: PropTypes.bool,
  /**
   * If set, clips the pill list after showing maxItems items
   */
  maxItems: PropTypes.number,
  /** Optional `FormattedMessage` id for a help tooltip next to the title */
  titleTooltipId: PropTypes.string,
}

function MultiSelectPillList(props) {
  const {
    id,
    title = null,
    titleTooltipId = null,
    options,
    onChange,
    values = [],
    formatOptionLabel = undefined,
    errors = [],
    titleClassName = '',
    titleAction = null,
    required = false,
    className = '',
    hidePills = false,
    isDisabled = false,
    maxItems = undefined,
    ...rest
  } = props ?? {}

  const toOption = useMemo(() => {
    const result = {}
    options?.forEach((option) => {
      result[option.value] = option
    })
    return result
  }, [options])

  const selectedOptions = values.map((value) => toOption[value])

  const onRemoveValue = useCallback(
    (valueToRemove) => {
      const result = values.filter((item) => item !== valueToRemove)
      onChange(result)
    },
    [onChange, values],
  )

  const getPillLabel = useCallback(
    (value) => {
      const option = toOption[value] || { label: value, value }
      return formatOptionLabel ? formatOptionLabel(option) : option?.label
    },
    [toOption, formatOptionLabel],
  )

  const renderPillList = () => {
    if (isDisabled) {
      return values.map((value) => <Pill key={value}>{getPillLabel(value)}</Pill>)
    }

    const items = values.map((value) => ({ label: getPillLabel(value), value }))
    return <PillList items={items} onRemoval={onRemoveValue} maxItems={maxItems} hideNoneLabel />
  }

  return (
    <div className="mb-3">
      {(title || titleAction) && (
        <div className="d-flex align-items-center mb-2">
          {title && (
            <p className={clsx('mb-0', titleClassName)}>
              <FormattedMessage id={title} />
              {titleTooltipId && <FieldHelpIcon tooltipId={titleTooltipId} />}
              &nbsp;
              {required && <span className="font-size-regular text-color-danger">*</span>}
            </p>
          )}

          {titleAction && <div className="ms-2 d-inline-flex align-items-center">{titleAction}</div>}
        </div>
      )}

      <MultiSelectSearch
        id={id}
        options={options}
        onChange={onChange}
        value={selectedOptions}
        formatOptionLabel={formatOptionLabel}
        className={clsx('mb-2', errors?.length && 'select-error-border', className)}
        isDisabled={isDisabled}
        {...rest}
      />

      {errors?.map((errorMessage) => (
        <div key={errorMessage} className="text-color-danger mb-2">
          {errorMessage}
        </div>
      ))}

      {!hidePills && values?.length > 0 && <div style={{ marginBottom: '-0.5rem' }}>{renderPillList()}</div>}
    </div>
  )
}

MultiSelectPillList.propTypes = propTypes

export default MultiSelectPillList
